<template>
    <div v-if="visible" class="baggage-section" :class="showIncludedPerSection && 'included'">
        <Info :icon="icon" :title="title" :description="description">
            <div v-if="showIncludedPerSection" class="baggage-badge hide-mobile">
                {{ t({ key: 'checkout.included' }) }}
            </div>
        </Info>

        <Included
            v-if="hasIncluded"
            :items="included"
            :type="type"
            :showIncludedPerItem="!showIncludedPerSection"
            :split="splitIncluded"
            :showTitle="showTitle"
            :showLabels="showLabels"
            :noBaggageSelectionLabel="noBaggageSelectionLabel"
            :noBaggageSelectionCabinBagLabel="noBaggageSelectionCabinBagLabel"
            :noBaggageSelectionCheckedBagLabel="noBaggageSelectionCheckedBagLabel" />

        <div v-if="showIncludedPerSection" class="baggage-badge show-mobile">
            {{ t({ key: 'checkout.included' }) }}
        </div>

        <Divider v-if="showDivider" color="lightGrey" thickness="1" />

        <Alternatives
            v-if="hasOptions"
            :type="type"
            :icon="icon"
            :onSelection="onSelection"
            :items="options"
            :includedItems="included"
            :split="splitAlternatives"
            :showTitle="showTitle"
            :showLabels="showLabels"
            :isOneWay="isOneWay"
            :noBaggageSelectionLabel="noBaggageSelectionLabel"
            :noBaggageSelectionCabinBagLabel="noBaggageSelectionCabinBagLabel"
            :noBaggageSelectionCheckedBagLabel="noBaggageSelectionCheckedBagLabel" />
    </div>
</template>

<script lang="ts">
    import { t } from '@shared/helpers/translation';
    import { computed, defineComponent, PropType } from 'vue';
    import { isEmpty } from 'lodash';
    import { Type as IconType } from '@ui/components/Icon/Icon.interfaces';
    import { SelectionMetaData, NoSelectionType } from '../BaggageSelectionForm.interfaces';
    import { Baggage, BaggageIncluded, BaggageType } from '@shared/interfaces/baggage';
    import Info from '../Shared/Info.vue';
    import Included from './Included/Included.vue';
    import Alternatives from './Alternatives/Alternatives.vue';
    import Divider from '@ui/components/Divider/Divider.vue';

    export default defineComponent({
        name: 'Section',
        methods: { t },
        components: {
            Divider,
            Info,
            Included,
            Alternatives,
        },
        props: {
            showLabels: { type: Boolean, default: false },
            splitIncluded: { type: Boolean, default: false },
            splitAlternatives: { type: Boolean, default: false },
            isOneWay: { type: Boolean, default: false },
            icon: { type: String as PropType<IconType>, default: '' },
            title: { type: String },
            description: { type: String },
            options: { type: Array as PropType<Baggage[]>, default: () => [] },
            included: {
                type: Array as PropType<BaggageIncluded[]>,
                default: () => [],
            },
            type: {
                type: String as PropType<BaggageType>,
                required: true,
            },
            onSelection: {
                required: true,
                type: Function as PropType<
                    (
                        selection: Baggage | NoSelectionType,
                        ids: string[],
                        meta: SelectionMetaData
                    ) => void
                >,
            },
            noBaggageSelectionCabinBagLabel: { type: String, required: true },
            noBaggageSelectionCheckedBagLabel: { type: String, required: true },
            noBaggageSelectionLabel: { type: String, required: true },
        },
        setup(props) {
            const hasOptions = computed((): boolean => !isEmpty(props.options));

            const hasIncluded = computed((): boolean => !isEmpty(props.included));

            const visible = computed((): boolean => hasIncluded.value || hasOptions.value);

            const showDivider = computed(
                () =>
                    hasIncluded.value &&
                    hasOptions.value &&
                    (props.splitAlternatives || props.splitIncluded)
            );

            const showTitle = computed(() => hasIncluded.value && hasOptions.value);

            const showIncludedPerSection = computed((): boolean => {
                const hasNoOptions = hasIncluded.value && !hasOptions.value;

                if (props.isOneWay || (!props.splitIncluded && hasIncluded.value)) {
                    return hasNoOptions;
                }

                const hasOutbound = props.included.some((x) =>
                    x.PerSector?.some((s) => s.SectorNumber === 1)
                );

                const hasInbound = props.included.some((x) =>
                    x.PerSector?.some((s) => s.SectorNumber === 2)
                );

                return hasNoOptions && hasOutbound === hasInbound;
            });

            return {
                visible,
                hasOptions,
                hasIncluded,
                showDivider,
                showTitle,
                showIncludedPerSection,
            };
        },
    });
</script>
